import React, { useEffect, useState } from "react";
import { useAppContext } from "../../../../../context/AppContext";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import Transition from "../../../../../components/Transition";
import { AnimatePresence } from "framer-motion";
import { AppAnnouncementData } from "../../../network/api/AppAnnouncementData";

const formatDateTimeLocal = (value) => {
    if (!value) {
        return "";
    }

    const normalizedValue = typeof value === "string" ? value.replace(" ", "T") : value;
    const parsed = new Date(normalizedValue);

    if (Number.isNaN(parsed.getTime())) {
        return "";
    }

    const pad = (number) => String(number).padStart(2, "0");

    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const formatApiDateTime = (value) => {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const pad = (number) => String(number).padStart(2, "0");

    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:00`;
};

const toBooleanFlag = (value) => value === true || value === 1 || value === "1";

export default function AnnouncementForm({
    setEditMode,
    selectedAnnouncement = null,
    canManageAnnouncements = false,
}) {
    const { appData } = useAppContext();
    const topicName = "trading_alerts";
    const [title, setTitle] = useState(selectedAnnouncement?.title || "");
    const [message, setMessage] = useState(selectedAnnouncement?.message || "");
    const [type, setType] = useState(selectedAnnouncement?.type || "info");
    const [isActive, setIsActive] = useState(String(selectedAnnouncement?.is_active ?? "1"));
    const [sendToTradingAlerts, setSendToTradingAlerts] = useState(toBooleanFlag(selectedAnnouncement?.send_to_trading_alerts));
    const [startsAt, setStartsAt] = useState(formatDateTimeLocal(selectedAnnouncement?.starts_at));
    const [endsAt, setEndsAt] = useState(formatDateTimeLocal(selectedAnnouncement?.ends_at));
    const [isLoading, setIsLoading] = useState(false);
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
        visible: false,
    });

    useEffect(() => {
        setTitle(selectedAnnouncement?.title || "");
        setMessage(selectedAnnouncement?.message || "");
        setType(selectedAnnouncement?.type || "info");
        setIsActive(String(selectedAnnouncement?.is_active ?? "1"));
        setSendToTradingAlerts(toBooleanFlag(selectedAnnouncement?.send_to_trading_alerts));
        setStartsAt(formatDateTimeLocal(selectedAnnouncement?.starts_at));
        setEndsAt(formatDateTimeLocal(selectedAnnouncement?.ends_at));
    }, [selectedAnnouncement]);

    const handleSubmit = async () => {
        if (!canManageAnnouncements) {
            setToastConfig({
                message: "Trusted super admin access is required to manage announcements.",
                type: "danger",
                visible: true,
            });
            return;
        }

        if (!title.trim() || !message.trim()) {
            setToastConfig({
                message: "Title and message are required.",
                type: "danger",
                visible: true,
            });
            return;
        }

        if (startsAt && endsAt && new Date(startsAt) > new Date(endsAt)) {
            setToastConfig({
                message: "End date must be after the start date.",
                type: "danger",
                visible: true,
            });
            return;
        }

        setIsLoading(true);

        const method = selectedAnnouncement?.id ? "PATCH" : "POST";
        const payload = {
            ...(selectedAnnouncement?.id ? { id: selectedAnnouncement.id } : { app_id: appData?.id }),
            title: title.trim(),
            message: message.trim(),
            type,
            is_active: Number(isActive),
            send_to_trading_alerts: sendToTradingAlerts ? 1 : 0,
            starts_at: formatApiDateTime(startsAt),
            ends_at: formatApiDateTime(endsAt),
        };

        const response = await AppAnnouncementData({
            method,
            appId: appData?.id,
            id: selectedAnnouncement?.id,
            formData: payload,
        });

        setIsLoading(false);

        if (response.status === "success") {
            setToastConfig({
                message: selectedAnnouncement?.id ? "Announcement updated successfully." : "Announcement created successfully.",
                type: "success",
                visible: true,
            });

            setTimeout(() => {
                setEditMode(false);
            }, 800);
        } else {
            setToastConfig({
                message: response.message || "Unable to save announcement.",
                type: "danger",
                visible: true,
            });
        }
    };

    return (
        <AnimatePresence mode="wait">
            <Transition key="announcement-form-page">
                <Toast
                    message={toastConfig.message}
                    type={toastConfig.type}
                    visible={toastConfig.visible}
                    setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                />

                <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="card-title">{selectedAnnouncement?.id ? "Edit" : "Create"} Announcement</h4>
                        <button className="btn btn-primary btn-sm" onClick={() => setEditMode(false)}>
                            <i className="fa fa-arrow-left"></i> Back
                        </button>
                    </div>

                    <div className="card-body">
                        {!canManageAnnouncements && (
                            <div className="alert alert-warning alert-alt solid">
                                Trusted super admin access is required for create and update actions.
                            </div>
                        )}

                        <div className="row">
                            <div className="mb-3 col-lg-6 col-12">
                                <label className="form-label">Title</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={title}
                                    onChange={(event) => setTitle(event.target.value)}
                                    placeholder="System Maintenance"
                                    disabled={!canManageAnnouncements || isLoading}
                                />
                            </div>

                            <div className="mb-3 col-lg-3 col-md-6 col-12">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    value={type}
                                    onChange={(event) => setType(event.target.value)}
                                    disabled={!canManageAnnouncements || isLoading}
                                >
                                    <option value="info">Info</option>
                                    <option value="success">Success</option>
                                    <option value="warning">Warning</option>
                                    <option value="error">Error</option>
                                </select>
                            </div>

                            <div className="mb-3 col-lg-3 col-md-6 col-12">
                                <label className="form-label">Status</label>
                                <select
                                    className="form-select"
                                    value={isActive}
                                    onChange={(event) => setIsActive(event.target.value)}
                                    disabled={!canManageAnnouncements || isLoading}
                                >
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                            </div>

                            <div className="mb-3 col-12">
                                <label className="form-label d-block">Delivery</label>
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendToTradingAlerts"
                                        checked={sendToTradingAlerts}
                                        onChange={(event) => setSendToTradingAlerts(event.target.checked)}
                                        disabled={!canManageAnnouncements || isLoading}
                                    />
                                    <label className="form-check-label" htmlFor="sendToTradingAlerts">
                                        Send this announcement to the <code>{topicName}</code> topic
                                    </label>
                                </div>
                                <div className="form-text">
                                    When enabled, the backend should publish this announcement to Firebase topic
                                    <code className="ms-1">{topicName}</code>.
                                </div>
                            </div>

                            <div className="mb-3 col-12">
                                <label className="form-label">Message</label>
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    value={message}
                                    onChange={(event) => setMessage(event.target.value)}
                                    placeholder="Admin withdrawals will be paused tonight from 22:00 to 23:00."
                                    disabled={!canManageAnnouncements || isLoading}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-12">
                                <label className="form-label">Starts At</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={startsAt}
                                    onChange={(event) => setStartsAt(event.target.value)}
                                    disabled={!canManageAnnouncements || isLoading}
                                />
                            </div>

                            <div className="mb-3 col-lg-6 col-12">
                                <label className="form-label">Ends At</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={endsAt}
                                    onChange={(event) => setEndsAt(event.target.value)}
                                    disabled={!canManageAnnouncements || isLoading}
                                />
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={!canManageAnnouncements || isLoading}
                        >
                            {isLoading ? <ButtonLoader /> : <><i className="fa fa-save me-2"></i>Save Announcement</>}
                        </button>
                    </div>
                </div>
            </Transition>
        </AnimatePresence>
    );
}
